import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  Box, Button, Checkbox, Flex, Text, useToast,
} from "@chakra-ui/react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabaseClient";
import { useUser } from "../../contexts/UserContext";
import { isConsentTarget, isImpersonating, hashDocumentText, latestConsentByPolicy } from "../../lib/policyConsentRules";
import useTierRolloutNotice from "../../hooks/useTierRolloutNotice";
import { rolloutSegment, FORCED_STATES } from "../../lib/tierRolloutNotice";
import {
  PAID_CHANGE, isPaidChangeConsentWindow, paidChangeConsentCopy, canonicalPaidChangeText, readForcedPaidConsent,
} from "../../lib/legalDocuments";
import { tt } from "../../i18n/tt";

// ============================================================================
// 유료 전환 별도 동의 — 전자상거래법 §13⑥(정기결제 개시·전환 전 30일 이내 별도 동의) · 약관 제18조 4~6항 · 부칙 3
//
// 창: 2026-10-01 ~ 10-31 (legalDocuments.isPaidChangeConsentWindow). 첫 청구 11/1 전에 한 번.
// 대상: 크리에이터(isConsentTarget) · 대리 접속 아님 · 등급제 안내 상태(get_tier_rollout_notice_state)가 「기존 회원 + 멤버십 구독료 등급」(paid, monthlyFee>0)
//       · paid_change v1.0 동의 기록 없음. 상태 조회 실패 → 안 띄운다.
// 닫힘: 「동의」 또는 「나중에」(같은 화면에서는 다시 안 띄움, 다른 화면 진입 시 재노출 — PolicyConsentGate 와 같은 규칙).
// 기록: user_policy_consent(paid_change, v1.0, document_hash = 등급·금액이 들어간 문안의 SHA-256, method=popup).
// 미동의자의 11/1 처리(청구 없음·신규 신청 정지)는 서버 청구 로직 몫 — 이 게이트는 동의 수집만 한다.
// QA: ?paidconsent=1 (기록은 남긴다 — 실제 동의 화면과 같다. 검토는 어드민 계정으로).
// ============================================================================

export default function PaidChangeConsentGate() {
  const { userInfo } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const email = userInfo?.email;
  const role = userInfo?.role;
  const forced = readForcedPaidConsent(location.search);
  const inWindow = forced || isPaidChangeConsentWindow();
  const target = Boolean(email) && isConsentTarget(role) && !isImpersonating() && inWindow && !location.pathname.startsWith("/policies");

  const { notice } = useTierRolloutNotice(email, { enabled: target && !forced });
  const state = forced ? FORCED_STATES.paid : notice;
  const segment = rolloutSegment(state);
  const applicable = target && state && segment === "paid" && Number(state.monthlyFee) > 0;

  const [consented, setConsented] = useState(null); // null=조회 전, true/false
  const [deferredPath, setDeferredPath] = useState(null);
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!applicable) return;
    try {
      const { data, error } = await supabase
        .from("user_policy_consent")
        .select("policy_key, version, agreed, method, created_at")
        .eq("user_email", email)
        .eq("policy_key", PAID_CHANGE.key);
      if (error) return setConsented(true); // 조회 실패 → 띄우지 않는다
      const latest = latestConsentByPolicy(data)[PAID_CHANGE.key];
      setConsented(Boolean(latest && latest.agreed && latest.version === PAID_CHANGE.version));
    } catch {
      setConsented(true);
    }
  }, [applicable, email]);

  useEffect(() => { load(); }, [load]);

  const copy = useMemo(() => (state ? paidChangeConsentCopy({ tier: state.tier, monthlyFee: state.monthlyFee }) : null), [state]);
  const open = applicable && consented === false && deferredPath !== location.pathname && Boolean(copy);

  const agree = async () => {
    if (!checked || busy) return;
    setBusy(true);
    try {
      const documentHash = await hashDocumentText(canonicalPaidChangeText({ tier: state.tier, monthlyFee: state.monthlyFee }));
      const { error } = await supabase.from("user_policy_consent").insert({
        user_email: email, policy_key: PAID_CHANGE.key, version: PAID_CHANGE.version, document_hash: documentHash, agreed: true, method: "popup",
      });
      if (error) throw error;
      setConsented(true);
      toast({ title: tt("a.7b4e3ed8", "동의가 기록되었습니다."), status: "success" });
    } catch (e) {
      toast({ title: tt("a.2fd3c57c", "동의를 기록하지 못했습니다."), description: e.message, status: "error" });
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <Modal isOpen onClose={() => {}} closeOnOverlayClick={false} closeOnEsc={false} isCentered size="lg">
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent mx={3} borderRadius="12px">
        <ModalHeader pb={1} fontSize="18px" letterSpacing="-0.02em">{copy.title}</ModalHeader>
        <ModalBody>
          <Text fontSize="14px" color="neutral.600" lineHeight="1.75" mb={4}>
            {tt("a.3d23be2d", "10월 31일까지는 청구가 없고, 11월 1일 확정 등급부터 멤버십 구독료가 시작됩니다. 결제가 시작되기 전에 아래 내용을 확인하고 동의해 주세요.")}
          </Text>
          <Box as="dl" border="1px solid" borderColor="neutral.200" borderRadius="10px" px={4} py={3} mb={4}
            display="grid" gridTemplateColumns="88px 1fr" rowGap={2} columnGap={3} fontSize="13.5px">
            {copy.lines.map((l) => (
              <React.Fragment key={l.k}>
                <Text as="dt" color="neutral.500">{l.k}</Text>
                <Text as="dd" color="neutral.800" fontWeight={l.k === "월 멤버십 구독료" ? "700" : "400"}>{l.v}</Text>
              </React.Fragment>
            ))}
          </Box>
          <Checkbox colorScheme="brand" isChecked={checked} onChange={(e) => setChecked(e.target.checked)} alignItems="flex-start">
            <Text fontSize="14px" fontWeight="600" lineHeight="1.6">{copy.consent}</Text>
          </Checkbox>
          <Text fontSize="12.5px" color="neutral.500" mt={3} lineHeight="1.6">{copy.note}</Text>
        </ModalBody>
        <ModalFooter gap={2} flexDirection={{ base: "column", sm: "row" }} alignItems="stretch">
          <Button variant="ghost" onClick={() => setDeferredPath(location.pathname)} flex={1}>{tt("a.a26db980", "나중에")}</Button>
          <Button variant="outline" onClick={() => navigate("/tier-guide")} flex={1}>{tt("a.87c7c3d8", "멤버십 구독료 안내 보기")}</Button>
          <Button colorScheme="brand" isDisabled={!checked} isLoading={busy} onClick={agree} flex={1}>{tt("a.38d9ffc3", "동의")}</Button>
        </ModalFooter>
        <Flex justify="center" pb={3}>
          <Text fontSize="11.5px" color="neutral.400">{tt("a.66a28696", "약관 제18조 · 부칙 3 · 전자상거래법 제13조 제6항에 따른 별도 동의")}</Text>
        </Flex>
      </ModalContent>
    </Modal>
  );
}
